import { useId } from 'react';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { SingleSelect } from 'src/components/common/SingleSelect';
import { useAllAgentDefinitions } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { PageIconPicker } from 'src/components/task-pages/PageIconPicker';
import { EditorDialogShell } from '../../EditorDialogShell';
import { PagePlacementAudienceFields } from '../shared/PagePlacementAudienceFields';
import {
  isAgentPageDraftDirty,
  isAgentPageDraftValid,
  type AgentPageDraft,
} from './agentPageAdmin';

/** The editor's form body — only mounted while the dialog is open. */
function AgentPageEditorFields({
  draft,
  onChange,
}: {
  draft: AgentPageDraft;
  onChange: (next: AgentPageDraft) => void;
}) {
  const gt = useGT();
  const idPrefix = useId();
  const { data: definitionsData } = useAllAgentDefinitions();

  const agentOptions = (definitionsData?.definitions ?? []).map((d) => ({
    id: d.id,
    label: d.name,
  }));

  return (
    <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-6 py-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-agent`}>
          <T context="agent page form field label">Agent</T>
        </Label>
        <SingleSelect
          id={`${idPrefix}-agent`}
          options={agentOptions}
          value={
            agentOptions.find((o) => o.id === draft.agentDefinitionId) ?? null
          }
          onChange={(o) =>
            onChange({
              ...draft,
              agentDefinitionId: o?.id ?? '',
              // Default the page name to the agent's name until the user types one.
              name: draft.name.trim() ? draft.name : (o?.label ?? ''),
            })
          }
          placeholder={gt('Select an agent', {
            context: 'agent picker placeholder in the agent page editor',
          })}
          fullWidth
        />
        <p className="text-[11px] text-muted-foreground">
          <T context="hint under the agent page agent field">
            The page shows this agent's live view, reasoning, and task queue,
            and lets people run it.
          </T>
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-name`}>
          <T context="agent page form field label">Name</T>
        </Label>
        <Input
          id={`${idPrefix}-name`}
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          placeholder={gt('Invoice Processor', {
            context: 'example agent page name placeholder',
          })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-description`}>
          <T context="agent page form field label">Description</T>
        </Label>
        <Textarea
          id={`${idPrefix}-description`}
          value={draft.description}
          rows={2}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>
          <T context="agent page form field label">Icon</T>
        </Label>
        <PageIconPicker
          value={draft.icon}
          onChange={(icon) => onChange({ ...draft, icon })}
        />
      </div>
      <PagePlacementAudienceFields
        idPrefix={idPrefix}
        draft={draft}
        onChange={onChange}
        audienceLabel={<T context="agent page form field label">Audience</T>}
        audiencePlaceholder={gt('Everyone with agent access', {
          context:
            'audience picker placeholder in the agent page editor, shown when no user groups are selected',
        })}
        audienceHint={
          <T context="hint under the agent page audience field">
            Limit the page to these user groups. Leave empty to show it to
            everyone who can view background agents.
          </T>
        }
      />
    </div>
  );
}

/**
 * Create/edit dialog for one agent page: the agent it mounts, identity,
 * sidebar placement, and the user-group audience (empty = every reader).
 * Mirrors the task page editor dialog.
 */
export function AgentPageEditorDialog({
  draft,
  initialDraft,
  onChange,
  onClose,
  onSave,
  isSaving,
}: {
  draft: AgentPageDraft | null;
  /** The draft as opened, for the unsaved-changes warning. */
  initialDraft: AgentPageDraft | null;
  onChange: (next: AgentPageDraft) => void;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const gt = useGT();
  const dirty =
    draft !== null &&
    initialDraft !== null &&
    isAgentPageDraftDirty(draft, initialDraft);
  const canSave = draft !== null && isAgentPageDraftValid(draft);
  const isEditing = draft?.id != null;

  return (
    <EditorDialogShell
      open={draft !== null}
      dirty={dirty}
      onClose={onClose}
      title={
        isEditing ? (
          <T context="dialog title, edit an agent page">Edit agent page</T>
        ) : (
          <T context="dialog title, create an agent page">New agent page</T>
        )
      }
      discard={{
        title: gt('Discard unsaved changes?', {
          context: 'unsaved-changes leave warning dialog title',
        }),
        message: gt(
          'Discard unsaved changes to this agent page? Your edits will be lost.',
          {
            context:
              'confirm dialog before closing the agent page editor with unsaved changes',
          },
        ),
        confirmText: gt('Leave without saving', {
          context: 'unsaved-changes leave warning, confirm button',
        }),
        cancelText: gt('Keep editing', {
          context: 'unsaved-changes leave warning, cancel button',
        }),
      }}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose} disabled={isSaving}>
            <T context="button, close the agent page editor">Cancel</T>
          </Button>
          <Button disabled={!canSave || isSaving} onClick={onSave}>
            {isSaving ? (
              <T context="button label while saving an agent page">Saving…</T>
            ) : isEditing ? (
              <T context="button, save the agent page changes">Save</T>
            ) : (
              <T context="button, create the agent page">Create</T>
            )}
          </Button>
        </>
      )}
    >
      {draft && <AgentPageEditorFields draft={draft} onChange={onChange} />}
    </EditorDialogShell>
  );
}
